import React, { useState, useEffect } from 'react';
import {
  LayoutGrid, Plus, Edit2, Trash2, ToggleLeft, ToggleRight,
  Loader2, X, Check, BarChart2, Users, School, Trophy,
  Gamepad2, TrendingUp, Clock, Star, ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../api';

const LEVELS = ['Primary 1-3', 'Primary 4-6', 'JSS 1-3', 'SS 1-3', 'All Levels'];
const CATS   = ['Math', 'English', 'Science', 'Social Studies', 'Spelling', 'General Knowledge'];

// ── GameModal ──────────────────────────────────────────────────────────────────
function GameModal({ game, onClose, onSaved }) {
  // Normalise legacy schema: name→title, available_bands→class_levels
  const normalise = (g) => g ? {
    ...g,
    title: g.title || g.name || '',
    class_levels: g.class_levels || g.available_bands || [],
    category: g.category || 'General',
    embed_url: g.embed_url || '',
    description: g.description || '',
    is_active: g.is_active !== false,
  } : null;

  const [form, setForm] = useState(
    game ? normalise(game) : { title: '', category: 'Math', class_levels: ['All Levels'], description: '', embed_url: '', is_active: true }
  );
  const [saving, setSaving] = useState(false);

  const toggle = (level) => {
    setForm(f => ({
      ...f,
      class_levels: (f.class_levels || []).includes(level)
        ? (f.class_levels || []).filter(l => l !== level)
        : [...(f.class_levels || []), level]
    }));
  };

  const save = async () => {
    if (!form.title.trim()) { toast.error('Title required'); return; }
    setSaving(true);
    try {
      let res;
      if (game?.id) res = await api.put(`/games/admin/${game.id}`, form);
      else res = await api.post('/games/admin', form);
      if (res?.success) { toast.success(game?.id ? 'Game updated' : 'Game created'); onSaved(); }
      else { toast.error('Failed to save game'); }
    } catch { toast.error('Failed to save game'); }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="game-modal">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-lg p-6 space-y-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <h3 className="font-black text-slate-800 text-lg">{game?.id ? 'Edit Game' : 'Add Game'}</h3>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-slate-100"><X className="w-5 h-5 text-slate-400" /></button>
        </div>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-semibold text-slate-500 block mb-1">Title *</label>
            <input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
              className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm focus:ring-2 focus:ring-blue-200 focus:border-blue-400 outline-none"
              placeholder="Game title" data-testid="game-title-input" />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-500 block mb-1">Description</label>
            <textarea value={form.description || ''} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              rows={2} className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm focus:ring-2 focus:ring-blue-200 focus:border-blue-400 outline-none resize-none" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-slate-500 block mb-1">Category</label>
              <select value={form.category || 'Math'} onChange={e => setForm(f => ({ ...f, category: e.target.value }))}
                className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm outline-none" data-testid="game-category-select">
                {CATS.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-500 block mb-1">Embed URL</label>
              <input value={form.embed_url || ''} onChange={e => setForm(f => ({ ...f, embed_url: e.target.value }))}
                className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm focus:ring-2 focus:ring-blue-200 focus:border-blue-400 outline-none"
                placeholder="https://..." data-testid="game-embed-url-input" />
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-500 block mb-2">Class Levels</label>
            <div className="flex flex-wrap gap-2">
              {LEVELS.map(l => {
                const active = (form.class_levels || []).includes(l);
                return (
                  <button key={l} onClick={() => toggle(l)} type="button"
                    className={`text-xs font-semibold px-3 py-1.5 rounded-full border transition-colors ${active ? 'bg-[#2563eb] text-white border-transparent' : 'bg-white text-slate-600 border-slate-200 hover:border-blue-300'}`}
                    data-testid={`level-btn-${l.replace(/\s/g, '-')}`}>
                    {l}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={() => setForm(f => ({ ...f, is_active: !f.is_active }))} type="button"
              className={`flex items-center gap-2 text-sm font-semibold ${form.is_active ? 'text-emerald-600' : 'text-slate-400'}`}
              data-testid="game-active-toggle">
              {form.is_active ? <ToggleRight className="w-5 h-5" /> : <ToggleLeft className="w-5 h-5" />}
              {form.is_active ? 'Active' : 'Inactive'}
            </button>
          </div>
        </div>
        <div className="flex gap-3 pt-2">
          <button onClick={onClose} className="flex-1 py-2.5 rounded-xl border border-slate-200 text-sm font-semibold text-slate-600 hover:bg-slate-50" data-testid="game-cancel-btn">Cancel</button>
          <button onClick={save} disabled={saving}
            className="flex-1 py-2.5 rounded-xl bg-[#2563eb] hover:bg-[#1d58d4] text-white text-sm font-bold shadow-sm transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            data-testid="game-save-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
            Save Game
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Statistics Tab ─────────────────────────────────────────────────────────────
function StatsTab() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/games/admin/stats');
        if (res?.success) setStats(res.data);
      } catch {}
      setLoading(false);
    })();
  }, []);

  if (loading) return (
    <div className="flex justify-center py-20"><div className="w-8 h-8 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" /></div>
  );

  if (!stats) return (
    <div className="bg-white rounded-3xl border border-slate-100 py-16 text-center shadow-sm">
      <BarChart2 className="w-12 h-12 text-slate-200 mx-auto mb-3" />
      <p className="text-slate-500 font-bold">No game activity yet</p>
    </div>
  );

  const { total_plays, unique_players, active_schools, game_stats, school_stats, top_students, recent_plays } = stats;

  return (
    <div className="space-y-5" data-testid="game-stats-tab">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { icon: Gamepad2, label: 'Total Plays', value: total_plays.toLocaleString(), color: 'bg-purple-100 text-purple-600' },
          { icon: Users,    label: 'Unique Players', value: unique_players.toLocaleString(), color: 'bg-blue-100 text-blue-600' },
          { icon: School,   label: 'Active Schools', value: active_schools.toLocaleString(), color: 'bg-emerald-100 text-emerald-600' },
          { icon: Trophy,   label: 'Top Game', value: game_stats[0]?.game_title || '—', color: 'bg-amber-100 text-amber-600' },
        ].map(({ icon: Icon, label, value, color }) => (
          <div key={label} className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 flex items-center gap-3" data-testid={`stat-card-${label.replace(/\s/g, '-').toLowerCase()}`}>
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${color}`}>
              <Icon className="w-5 h-5" />
            </div>
            <div className="min-w-0">
              <p className="text-xs text-slate-500 font-medium">{label}</p>
              <p className="font-black text-slate-800 text-base truncate">{value}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Per-Game Performance */}
        <div className="bg-white rounded-3xl border border-slate-100 shadow-sm overflow-hidden" data-testid="game-performance-table">
          <div className="px-5 py-4 border-b border-slate-50 flex items-center gap-2">
            <BarChart2 className="w-4 h-4 text-purple-600" />
            <p className="text-sm font-bold text-slate-700">Game Performance</p>
          </div>
          {game_stats.length === 0 ? (
            <p className="text-center py-10 text-slate-400 text-sm">No plays recorded yet</p>
          ) : (
            <div className="divide-y divide-slate-50">
              {game_stats.map((g, i) => {
                const maxPlays = game_stats[0]?.total_plays || 1;
                const pct = Math.round((g.total_plays / maxPlays) * 100);
                return (
                  <div key={g.game_id || i} className="px-5 py-3">
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-sm font-semibold text-slate-700 truncate max-w-[160px]">{g.game_title}</span>
                      <div className="flex items-center gap-3 text-xs text-slate-500 flex-shrink-0">
                        <span className="flex items-center gap-1"><Gamepad2 className="w-3 h-3" />{g.total_plays}</span>
                        <span className="flex items-center gap-1"><Users className="w-3 h-3" />{g.unique_players}</span>
                        <span className="flex items-center gap-1 text-amber-600 font-semibold"><Star className="w-3 h-3" />{g.top_score}</span>
                      </div>
                    </div>
                    <div className="h-1.5 bg-slate-100 rounded-full">
                      <div className="h-full bg-purple-500 rounded-full transition-all" style={{ width: `${pct}%` }} />
                    </div>
                    <p className="text-xs text-slate-400 mt-1">Avg score: {g.avg_score}</p>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Top Students */}
        <div className="bg-white rounded-3xl border border-slate-100 shadow-sm overflow-hidden" data-testid="top-students-table">
          <div className="px-5 py-4 border-b border-slate-50 flex items-center gap-2">
            <Trophy className="w-4 h-4 text-amber-500" />
            <p className="text-sm font-bold text-slate-700">Top Student Players</p>
          </div>
          {top_students.length === 0 ? (
            <p className="text-center py-10 text-slate-400 text-sm">No student activity yet</p>
          ) : (
            <div className="divide-y divide-slate-50">
              {top_students.map((s, i) => (
                <div key={i} className="px-5 py-3 flex items-center gap-3">
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-black flex-shrink-0 ${
                    i === 0 ? 'bg-amber-100 text-amber-700' :
                    i === 1 ? 'bg-slate-200 text-slate-600' :
                    i === 2 ? 'bg-orange-100 text-orange-700' : 'bg-slate-100 text-slate-500'
                  }`}>{i + 1}</div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-slate-700 truncate">{s.student_name}</p>
                    <p className="text-xs text-slate-400">{s.class_name || 'Unknown class'}</p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-sm font-bold text-purple-600">{s.total_plays} plays</p>
                    <p className="text-xs text-slate-400">Top: {s.top_score}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* School Engagement */}
        <div className="bg-white rounded-3xl border border-slate-100 shadow-sm overflow-hidden" data-testid="school-engagement-table">
          <div className="px-5 py-4 border-b border-slate-50 flex items-center gap-2">
            <School className="w-4 h-4 text-emerald-600" />
            <p className="text-sm font-bold text-slate-700">School Engagement</p>
          </div>
          {school_stats.length === 0 ? (
            <p className="text-center py-10 text-slate-400 text-sm">No school activity yet</p>
          ) : (
            <div className="divide-y divide-slate-50">
              {school_stats.map((s, i) => {
                const maxPlays = school_stats[0]?.total_plays || 1;
                const pct = Math.round((s.total_plays / maxPlays) * 100);
                return (
                  <div key={i} className="px-5 py-3">
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-sm font-semibold text-slate-700 truncate max-w-[180px]">{s.school_name}</span>
                      <div className="flex items-center gap-3 text-xs text-slate-500 flex-shrink-0">
                        <span className="flex items-center gap-1"><Gamepad2 className="w-3 h-3" />{s.total_plays}</span>
                        <span className="flex items-center gap-1"><Users className="w-3 h-3" />{s.unique_players}</span>
                      </div>
                    </div>
                    <div className="h-1.5 bg-slate-100 rounded-full">
                      <div className="h-full bg-emerald-500 rounded-full transition-all" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Recent Activity */}
        <div className="bg-white rounded-3xl border border-slate-100 shadow-sm overflow-hidden" data-testid="recent-activity-table">
          <div className="px-5 py-4 border-b border-slate-50 flex items-center gap-2">
            <Clock className="w-4 h-4 text-blue-600" />
            <p className="text-sm font-bold text-slate-700">Recent Activity</p>
          </div>
          {recent_plays.length === 0 ? (
            <p className="text-center py-10 text-slate-400 text-sm">No recent activity</p>
          ) : (
            <div className="divide-y divide-slate-50">
              {recent_plays.map((p, i) => (
                <div key={i} className="px-5 py-3 flex items-center gap-3">
                  <div className="w-8 h-8 rounded-xl bg-blue-50 flex items-center justify-center flex-shrink-0">
                    <Gamepad2 className="w-4 h-4 text-blue-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-slate-700 truncate">{p.student_name}</p>
                    <p className="text-xs text-slate-400 truncate">{p.game_title} · {p.class_name}</p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-sm font-bold text-blue-600">{p.score} pts</p>
                    <p className="text-xs text-slate-400">{new Date(p.played_at).toLocaleDateString()}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Main Page ──────────────────────────────────────────────────────────────────
export default function MasterGamesPage() {
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [deleting, setDeleting] = useState('');
  const [tab, setTab] = useState('games');

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get('/games/admin/all');
      if (res?.success) setGames(res.data || []);
    } catch {}
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const toggleActive = async (g) => {
    try {
      const res = await api.put(`/games/admin/${g.id}`, { ...g, is_active: !g.is_active });
      if (res?.success) { toast.success(g.is_active ? 'Game deactivated' : 'Game activated'); load(); }
    } catch { toast.error('Failed to update'); }
  };

  const del = async (id) => {
    if (!window.confirm('Delete this game?')) return;
    setDeleting(id);
    try {
      const res = await api.delete(`/games/admin/${id}`);
      if (res?.success) { toast.success('Deleted'); load(); }
    } catch { toast.error('Failed to delete'); }
    setDeleting('');
  };

  return (
    <div className="p-4 lg:p-6 max-w-5xl mx-auto space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-black text-slate-800 flex items-center gap-2" data-testid="master-games-title">
            <LayoutGrid className="w-7 h-7 text-purple-600" /> Games Management
          </h1>
          <p className="text-slate-500 text-sm mt-0.5">Manage educational games and view student engagement statistics</p>
        </div>
        {tab === 'games' && (
          <button onClick={() => setModal('add')}
            className="flex items-center gap-2 bg-[#2563eb] hover:bg-[#1d58d4] text-white font-bold px-5 py-2.5 rounded-xl shadow-sm transition-colors"
            data-testid="add-game-btn">
            <Plus className="w-4 h-4" /> Add Game
          </button>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-slate-100 rounded-xl p-1 w-fit" data-testid="games-tabs">
        {[
          { key: 'games', label: 'Games', icon: LayoutGrid },
          { key: 'stats', label: 'Statistics', icon: BarChart2 },
        ].map(({ key, label, icon: Icon }) => (
          <button key={key} onClick={() => setTab(key)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${tab === key ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
            data-testid={`tab-${key}`}>
            <Icon className="w-4 h-4" />{label}
          </button>
        ))}
      </div>

      {tab === 'stats' ? <StatsTab /> : (
        <>
          {loading ? (
            <div className="flex justify-center py-20">
              <div className="w-8 h-8 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : games.length === 0 ? (
            <div className="bg-white rounded-3xl border border-slate-100 py-16 text-center shadow-sm">
              <LayoutGrid className="w-12 h-12 text-slate-200 mx-auto mb-3" />
              <p className="text-slate-500 font-bold">No games yet</p>
              <p className="text-sm text-slate-400 mt-1">Click "+ Add Game" to get started</p>
            </div>
          ) : (
            <div className="bg-white rounded-3xl border border-slate-100 shadow-sm overflow-hidden">
              <div className="px-5 py-3 border-b border-slate-50">
                <p className="text-xs font-bold text-slate-500 uppercase tracking-wide">{games.length} Games Total</p>
              </div>
              <div className="divide-y divide-slate-50">
                {games.map(g => (
                  <div key={g.id} className="flex items-center gap-4 px-5 py-4 hover:bg-slate-50" data-testid={`game-row-${g.id}`}>
                    <div className="w-10 h-10 rounded-2xl bg-purple-100 flex items-center justify-center flex-shrink-0">
                      <LayoutGrid className="w-5 h-5 text-purple-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-slate-800 text-sm">{g.name || g.title}</p>
                      <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                        <span className="text-xs text-slate-400">{g.category || g.game_type || ''}</span>
                        {(g.class_levels || g.available_bands || []).slice(0, 3).map(l => (
                          <span key={l} className="text-xs bg-blue-50 text-blue-600 px-2 py-0.5 rounded-full font-medium">{l}</span>
                        ))}
                        {g.play_count > 0 && (
                          <span className="text-xs text-purple-600 font-medium flex items-center gap-1">
                            <Gamepad2 className="w-3 h-3" />{g.play_count} plays
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <button onClick={() => toggleActive(g)}
                        className={`flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-xl transition-colors ${g.is_active ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`}
                        data-testid={`toggle-game-${g.id}`}>
                        {g.is_active ? <ToggleRight className="w-4 h-4" /> : <ToggleLeft className="w-4 h-4" />}
                        {g.is_active ? 'Active' : 'Off'}
                      </button>
                      <button onClick={() => setModal(g)} className="text-slate-400 hover:text-[#2563eb] p-2 rounded-xl hover:bg-blue-50" data-testid={`edit-game-${g.id}`}>
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button onClick={() => del(g.id)} disabled={deleting === g.id} className="text-slate-400 hover:text-red-600 p-2 rounded-xl hover:bg-red-50" data-testid={`delete-game-${g.id}`}>
                        {deleting === g.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {modal && (
        <GameModal
          game={modal === 'add' ? null : modal}
          onClose={() => setModal(null)}
          onSaved={() => { setModal(null); load(); }}
        />
      )}
    </div>
  );
}
